/**
 * ProductReservation Web Component
 *
 * Submit flow:
 * POST JSON alla funzione Vercel → crea bozza d'ordine in Shopify Admin
 *
 * Success:
 * - Se data-success-url è impostato → redirect alla pagina di conferma
 * - Altrimenti → messaggio inline (fallback)
 */
class ProductReservation extends HTMLElement {
  constructor() {
    super();
    this._submitting = false; // guard anti-doppia submission
    this.ctaButton = this.querySelector('[ref="ctaButton"]');
    this.formWrapper = this.querySelector('[ref="formWrapper"]');
    this.submitButton = this.querySelector('[ref="submitButton"]');
    this.successMessage = this.querySelector('[ref="successMessage"]');
    this.errorMessage = this.querySelector('[ref="errorMessage"]');
    this.nameInput = this.querySelector('[ref="nameInput"]');
    this.emailInput = this.querySelector('[ref="emailInput"]');
    this.phoneInput = this.querySelector('[ref="phoneInput"]');
    this.form = this.querySelector('form');
  }

  connectedCallback() {
    if (this.ctaButton) {
      this.ctaButton.addEventListener('click', this._handleCtaClick.bind(this));
    }
    if (this.form) {
      this.form.addEventListener('submit', this._handleSubmit.bind(this));
    }
    this._setupColorSelector();
  }

  /* ── Color selector (standalone, senza customizer) ──────────── */
  _setupColorSelector() {
    const colorInputs = this.querySelectorAll('[name^="color-"]');
    if (!colorInputs.length) return;

    colorInputs.forEach((input) => {
      input.addEventListener('change', () => {
        // Toggle active class on all labels
        colorInputs.forEach((inp) => {
          const label = inp.closest('label');
          if (label) label.classList.toggle('active', inp.checked);
        });
        // Update selected label text
        const labelEl = this.querySelector('.product-color-selector__selected-label');
        if (labelEl && input.value) {
          labelEl.textContent = input.value;
        }
      });
    });
  }

  /** Apre il form con animazione smooth */
  _handleCtaClick() {
    this.ctaButton.setAttribute('aria-expanded', 'true');
    this.formWrapper.setAttribute('aria-hidden', 'false');
    setTimeout(() => {
      if (this.nameInput) this.nameInput.focus();
    }, 360);
  }

  async _handleSubmit(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    // Guard: evita doppia submission
    if (this._submitting) return;

    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    this._submitting = true;
    this._setLoading(true);

    const functionUrl = this.dataset.functionUrl;

    try {
      const res = await fetch(functionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: this.nameInput?.value?.trim(),
          email: this.emailInput?.value?.trim(),
          phone: this.phoneInput?.value?.trim() || null,
          productTitle: this.dataset.productTitle,
          productUrl: this.dataset.productUrl,
          productPriceCents: parseInt(this.dataset.priceCents, 10) || 0,
        }),
      });

      const data = await res.json().catch(() => ({}));
      console.log('[reservation] Vercel response:', res.status, data);

      if (data.success) {
        this._showSuccess();
        return;
      }

      console.warn('[reservation] Draft order failed:', data.error);
      this._showError();
    } catch (networkErr) {
      console.warn('[reservation] Network error:', networkErr);
      this._showError();
    }
  }

  _setLoading(loading) {
    this.submitButton.setAttribute('data-loading', loading ? 'true' : 'false');
    this.submitButton.disabled = loading;
  }

  _showSuccess() {
    this._setLoading(false);

    // Se c'è un URL di successo configurato → redirect
    const successUrl = this.dataset.successUrl;
    if (successUrl) {
      window.location.href = successUrl;
      return;
    }

    // Fallback: messaggio inline
    if (this.formWrapper) this.formWrapper.style.display = 'none';
    if (this.successMessage) {
      this.successMessage.setAttribute('aria-hidden', 'false');
      this.successMessage.style.display = 'block';
    }
  }

  _showError() {
    this._submitting = false;
    this._setLoading(false);
    if (this.errorMessage) {
      this.errorMessage.setAttribute('aria-hidden', 'false');
    }
  }
}

customElements.define('product-reservation', ProductReservation);
